import React from 'react';
import {
  Typography,
  Container,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Skeleton,
  Chip,
  Divider,
} from '@mui/material';
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { useTodoList } from '../hooks/useTodoList';

export const TodoHistoryPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const { completedTodos, loading: todosLoading, toggleTodo, deleteTodo } = useTodoList();

  // Formatar data
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  // Agrupar por mês de conclusão (updatedAt)
  const groupByMonth = () => {
    const groups: Record<string, typeof completedTodos> = {};
    
    completedTodos.forEach(todo => {
      const monthYear = new Intl.DateTimeFormat('pt-BR', {
        month: 'long',
        year: 'numeric',
      }).format(todo.updatedAt); // USAR updatedAt em vez de date
      
      if (!groups[monthYear]) {
        groups[monthYear] = [];
      }
      groups[monthYear].push(todo);
    });
    
    return groups;
  };

  const groupedTodos = groupByMonth();

  // Loading state
  if (authLoading) {
    return (
      <>
        <TopBar
          title="Histórico"
          icon={<HistoryIcon sx={{ fontSize: '0.875rem' }} />}
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
        <Box sx={{
          backgroundColor: theme.colors.background,
          minHeight: '100vh',
          paddingBottom: '120px',
          paddingTop: '70px',
        }}>
          <Container component="main" sx={{ flexGrow: 1, padding: '16px' }}>
            {[1, 2, 3, 4].map((item) => (
              <Skeleton key={item} variant="rectangular" height={80} sx={{ borderRadius: theme.radii.medium, mb: 2 }} />
            ))}
          </Container>
        </Box>
      </>
    );
  }

  // Require authentication
  if (!user) {
    navigate('/login');
    return null;
  }

  const handleUndo = async (todoId: string) => {
    try {
      await toggleTodo(todoId, false);
    } catch (error) {
      console.error('Erro ao desfazer tarefa');
    }
  };

  const handleDelete = async (todoId: string) => {
    try {
      await deleteTodo(todoId);
    } catch (error) {
      console.error('Erro ao deletar tarefa');
    }
  };

  return (
    <>
      <TopBar
        title="Histórico de Tarefas"
        icon={<HistoryIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        onBackClick={() => navigate(-1)}
      />

      <Box sx={{
        backgroundColor: theme.colors.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '120px',
        paddingTop: '70px',
      }}>
        <Container component="main" sx={{ flexGrow: 1, padding: '16px' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1 }}>
              Tarefas Concluídas
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              {completedTodos.length} {completedTodos.length === 1 ? 'tarefa concluída' : 'tarefas concluídas'}
            </Typography>
          </Box>

          {todosLoading ? (
            [1, 2, 3, 4, 5].map((item) => (
              <Skeleton key={item} variant="rectangular" height={80} sx={{ borderRadius: theme.radii.medium, mb: 2 }} />
            ))
          ) : completedTodos.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <HistoryIcon sx={{ fontSize: 64, color: theme.colors.text.secondary, opacity: 0.3, mb: 2 }} />
              <Typography sx={{ color: theme.colors.text.secondary, fontSize: '1rem', mb: 1 }}>
                Nenhuma tarefa concluída ainda
              </Typography>
              <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                Complete algumas tarefas para vê-las aqui!
              </Typography>
            </Box>
          ) : (
            Object.entries(groupedTodos).map(([monthYear, todos]) => (
              <Box key={monthYear} sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: theme.colors.text.primary,
                    textTransform: 'capitalize',
                    mb: 2 
                  }}
                >
                  {monthYear}
                </Typography>

                <List sx={{ 
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radii.medium,
                  overflow: 'hidden',
                }}>
                  {todos.map((todo, index) => (
                    <React.Fragment key={todo.id}>
                      <ListItem
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                              edge="end" 
                              onClick={() => handleUndo(todo.id)}
                              sx={{ color: theme.colors.primary }}
                              size="small"
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              edge="end" 
                              onClick={() => handleDelete(todo.id)}
                              sx={{ color: '#d32f2f' }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        sx={{ 
                          py: 2,
                          '&:hover': {
                            backgroundColor: theme.colors.background,
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography 
                              sx={{ 
                                textDecoration: 'line-through',
                                color: theme.colors.text.secondary,
                                mb: 0.5
                              }}
                            >
                              {todo.title}
                            </Typography>
                          }
                          secondary={
                            <Chip 
                              label={formatDate(todo.date)}
                              size="small"
                              sx={{ 
                                backgroundColor: theme.colors.secondary,
                                color: theme.colors.primary,
                                fontSize: '0.75rem',
                                height: 24,
                              }}
                            />
                          }
                        />
                      </ListItem>
                      {index < todos.length - 1 && (
                        <Divider sx={{ borderColor: theme.colors.border }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ))
          )}
        </Container>

        <BottomNavigation />
      </Box>
    </>
  );
};
